import { Enum_LiveStream_Status } from '@core/api';
import { isLiveStreamReplay } from '@core/livestream/helpers';
import uniqBy from '@core/lodash/uniqBy';
/**
 *
 */
export const highlightEntityReducer = (state, { liveStreamId, entityType, entityId, winners }) => {
    if (!entityType || !entityId) {
        return state;
    }
    const liveStream = state.liveStreams[liveStreamId];
    const updatedProducts = [
        ...(state.liveStreamProducts?.[liveStreamId] || []),
    ];
    if (entityType === 'product') {
        const product = updatedProducts.find((product) => product.id === entityId);
        if (product) {
            product.hidden = false;
        }
    }
    if (entityType === 'giveaway') {
        if (liveStream.open_entry_giveaway === entityId) {
            liveStream.open_entry_giveaway = undefined;
        }
    }
    const existingEntity = liveStream?.highlighted_entity;
    const existingEntities = liveStream?.highlighted_entities ?? [];
    /**
     * When the giveaway winner is drawn
     * we make the giveaway the highlighted entity
     * but if only one thing is currently highlighted
     * it lives in highlighted_entity and has to be replaced
     * so we move it to highlighted_entities
     */
    if (existingEntity &&
        existingEntities &&
        !existingEntities.find((ee) => ee.entity_id === existingEntity.entity_id)) {
        existingEntities.push(existingEntity);
    }
    return {
        liveStreamProducts: {
            ...state.liveStreamProducts,
            [liveStreamId]: updatedProducts,
        },
        liveStreams: {
            ...state.liveStreams,
            [liveStreamId]: {
                ...liveStream,
                highlighted_entity: {
                    entity_type: entityType,
                    entity_id: entityId,
                    winners,
                },
                highlighted_entities: existingEntities,
            },
        },
    };
};
/**
 * Highlights the specified entities and
 * sets the `hidden` property to `false` for all products that were highlighted
 */
export const highlightEntitiesReducer = (state, { liveStreamId, entities }) => {
    if (!entities || !entities.length) {
        return state;
    }
    const productEntities = entities.filter((entity) => entity.entity_type === 'product');
    const updatedProducts = [
        ...(state.liveStreamProducts?.[liveStreamId] || []),
    ];
    const productMap = {};
    updatedProducts.forEach((product) => {
        if (product.id) {
            productMap[product.id] = product;
        }
    });
    productEntities.forEach((entity) => {
        if (entity.entity_id) {
            const product = productMap[entity.entity_id];
            if (product) {
                product.hidden = false;
            }
        }
    });
    const existingEntity = state.liveStreams[liveStreamId]?.highlighted_entity ?? null;
    let existingEntities = [
        ...(state.liveStreams[liveStreamId]?.highlighted_entities ?? []),
        ...entities.map(({ entity_id, entity_type, winners, tally }) => ({
            entity_id,
            entity_type,
            winners,
            tally,
        })),
    ];
    if (existingEntity &&
        !existingEntities.find((ee) => ee.entity_id === existingEntity?.entity_id)) {
        existingEntities.push(existingEntity);
    }
    existingEntities = uniqBy(existingEntities, (he) => {
        return `${he.entity_id}-${he.entity_type}`;
    });
    if (entities.some((e) => e.entity_type === 'product') &&
        isLiveStreamReplay(state.video)) {
        existingEntities = existingEntities.filter((entity) => {
            if (entity.entity_type !== 'product') {
                return true;
            }
            // all products expected to be highlighted are sent every time when live
            // but when we seek through a replay, there might already be other
            // products highlighted from the last seek, so we only want to keep
            // the ones in the payload
            return (entities.find((e) => e.entity_id === entity.entity_id) !== undefined);
        });
    }
    // we want to skip highlighted_entity if it's a link and add directly to highlighted_entities
    if (entities.length === 1 && entities[0].entity_type !== 'link') {
        return {
            liveStreamProducts: {
                ...state.liveStreamProducts,
                [liveStreamId]: updatedProducts,
            },
            liveStreams: {
                ...state.liveStreams,
                [liveStreamId]: {
                    ...state.liveStreams[liveStreamId],
                    highlighted_entities: existingEntities,
                    highlighted_entity: entities[0],
                },
            },
        };
    }
    else {
        return {
            liveStreamProducts: {
                ...state.liveStreamProducts,
                [liveStreamId]: updatedProducts,
            },
            liveStreams: {
                ...state.liveStreams,
                [liveStreamId]: {
                    ...state.liveStreams[liveStreamId],
                    highlighted_entities: existingEntities,
                    highlighted_entity: existingEntity,
                },
            },
        };
    }
};
/**
 *
 */
export const unhighlightEntityReducer = (state, { liveStreamId, tally, entityId, entityType }) => unhighlightEntitiesReducer(state, {
    liveStreamId,
    entities: [{ tally, entity_type: entityType, entity_id: entityId }],
});
/**
 * checks if an entity exists in an array of entities
 */
function entityExists(entity, entities) {
    return entities.some((e) => e.entity_id === entity.entity_id && e.entity_type === entity.entity_type);
}
/**
 *
 */
export const unhighlightEntitiesReducer = (state, { liveStreamId, entities }) => {
    const liveStream = state.liveStreams[liveStreamId];
    if (!entities ||
        !entities.length ||
        !liveStream ||
        (!liveStream.highlighted_entities?.length &&
            !liveStream.highlighted_entity) ||
        !entities[0].entity_type) {
        return state;
    }
    // in current version of WS, we receive poll's tally in unhighlight event,
    // which feels bad, since in same action we should show results and hide poll.
    const poll = entities.find(({ entity_type }) => entity_type === 'poll');
    const pollFromLiveStream = liveStream?.highlighted_entities?.find(({ entity_type, entity_id }) => entity_type === 'poll' || entity_id === poll?.entity_id) || liveStream?.highlighted_entity;
    // we need to add the tally to the poll if it's in the highlighted entities
    // not just the entity
    let existingEntities = liveStream.highlighted_entities?.map((he) => {
        if (he.entity_id === pollFromLiveStream?.entity_id) {
            he.tally = poll?.tally;
        }
        return he;
    }) ?? [];
    /**
     * when unhighlighting a trivia_giveaway, we expect 3 unhighlight
     * messages in rapid succession. The time between messages is enough
     * to cause a noticable flicker of the interaction
     * So when we receive any ubnhighlight message for a trivia giveaway
     * entity we also unhighlight the others at the same time.
     */
    if (entities[0].entity_type.startsWith('trivia_giveaway')) {
        existingEntities = existingEntities.filter((e) => e?.entity_id !== entities[0].entity_id);
    }
    if (poll &&
        (liveStream?.status === Enum_LiveStream_Status.ACTIVE ||
            liveStream?.status === Enum_LiveStream_Status.PAUSED) &&
        poll.entity_id === pollFromLiveStream?.entity_id) {
        return {
            liveStreams: {
                ...state.liveStreams,
                [liveStreamId]: {
                    ...liveStream,
                    highlighted_entity: {
                        ...pollFromLiveStream,
                        tally: poll.tally,
                    },
                    highlighted_entities: existingEntities,
                },
            },
        };
    }
    const remainingHighlightedEntities = existingEntities.filter((entity) => !entityExists(entity, entities));
    const remainingHighlightedEntity = liveStream.highlighted_entity &&
        !entityExists(liveStream.highlighted_entity, entities) &&
        !(entities[0].entity_type === 'product' &&
            liveStream.highlighted_entity.entity_type === 'product')
        ? liveStream.highlighted_entity
        : null;
    switch (remainingHighlightedEntities.length) {
        case 0:
            return {
                liveStreams: {
                    ...state.liveStreams,
                    [liveStreamId]: {
                        ...state.liveStreams[liveStreamId],
                        highlighted_entities: [],
                        highlighted_entity: remainingHighlightedEntity,
                    },
                },
            };
        case 1:
            return {
                liveStreams: {
                    ...state.liveStreams,
                    [liveStreamId]: {
                        ...state.liveStreams[liveStreamId],
                        highlighted_entities: remainingHighlightedEntities,
                        highlighted_entity: remainingHighlightedEntity,
                    },
                },
            };
        default:
            return {
                liveStreams: {
                    ...state.liveStreams,
                    [liveStreamId]: {
                        ...state.liveStreams[liveStreamId],
                        highlighted_entities: remainingHighlightedEntities,
                        highlighted_entity: remainingHighlightedEntity,
                    },
                },
            };
    }
};
/**
 *
 */
export const enableEntityReducer = (state, { liveStreamId, entityId }) => {
    if (!entityId) {
        return state;
    }
    return {
        liveStreams: {
            ...state.liveStreams,
            [liveStreamId]: {
                ...state.liveStreams[liveStreamId],
                disabled_entities: (state.liveStreams[liveStreamId].disabled_entities ?? []).filter((entity) => entity.entity_id !== entityId),
            },
        },
    };
};
/**
 *
 */
export const disableEntityReducer = (state, { liveStreamId, entityId, entityType }) => {
    if (!entityType || !entityId) {
        return state;
    }
    return {
        liveStreams: {
            ...state.liveStreams,
            [liveStreamId]: {
                ...state.liveStreams[liveStreamId],
                disabled_entities: [
                    ...(state.liveStreams[liveStreamId].disabled_entities ?? []),
                    {
                        entity_id: entityId,
                        entity_type: entityType,
                    },
                ],
            },
        },
    };
};
/**
 *
 */
export const setLiveStreamPinnedMessageReducer = (state, { liveStreamId, message }) => {
    return {
        liveStreamPinnedMessage: {
            ...state.liveStreamPinnedMessage,
            [liveStreamId]: message,
        },
    };
};
/**
 *
 */
export const unsetLiveStreamPinnedMessageReducer = (state, { liveStreamId }) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { [liveStreamId]: value, ...newState } = state.liveStreamPinnedMessage;
    return {
        liveStreamPinnedMessage: {
            ...newState,
        },
    };
};
/**
 *
 */
export const setLiveStreamTemporaryChatHighlightMessageReducer = (state, { liveStreamId, message }) => {
    return {
        liveStreamTemporaryChatHighlightMessage: {
            ...state.liveStreamTemporaryChatHighlightMessage,
            [liveStreamId]: message,
        },
    };
};
/**
 *
 */
export const unsetLiveStreamTemporaryChatHighlightMessageReducer = (state, { liveStreamId }) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { [liveStreamId]: value, ...newState } = state.liveStreamTemporaryChatHighlightMessage;
    return {
        liveStreamTemporaryChatHighlightMessage: {
            ...newState,
        },
    };
};
/**
 *
 */
export const giveawayCollectionReducer = (state, { liveStreamId, openGiveaway }) => {
    if (!openGiveaway) {
        return state;
    }
    return {
        liveStreams: {
            ...state.liveStreams,
            [liveStreamId]: {
                ...state.liveStreams[liveStreamId],
                open_entry_giveaway: openGiveaway,
                highlighted_entities: state.liveStreams[liveStreamId].highlighted_entities,
                highlighted_entity: state.liveStreams[liveStreamId].highlighted_entity,
            },
        },
    };
};
